import React, { useState } from "react";
import { motion } from "framer-motion";
import { Progress } from "../components/ui/progress";
import ShareStatsCard from "../components/ShareStatsCard";
import ShareableInfographics from "../components/ShareableInfographics";

const EmotionalSpendingTriggerQuiz = ({ theme, goToInput }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultData, setResultData] = useState(null);
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Emotional spending trigger assessment questions
  const questions = [
    {
      id: "emotion_spend",
      text: "How often do you shop when experiencing strong emotions?",
      options: [
        { value: "rarely", label: "Rarely or never" },
        { value: "occasionally", label: "Occasionally" },
        { value: "often", label: "Often" },
        { value: "very_often", label: "Very often" }
      ]
    },
    {
      id: "negative_emotions",
      text: "Which negative emotions are most likely to trigger a shopping urge for you?",
      options: [
        { value: "sadness", label: "Sadness or depression" },
        { value: "anxiety", label: "Anxiety or stress" },
        { value: "boredom", label: "Boredom or emptiness" },
        { value: "loneliness", label: "Loneliness or isolation" }
      ]
    },
    {
      id: "positive_emotions",
      text: "Which positive emotions are most likely to trigger a shopping urge for you?",
      options: [
        { value: "excitement", label: "Excitement or enthusiasm" },
        { value: "happiness", label: "Happiness or joy" },
        { value: "pride", label: "Pride or sense of achievement" },
        { value: "relief", label: "Relief or relaxation" }
      ]
    },
    {
      id: "emotional_intensity",
      text: "How intense are your shopping urges when emotionally triggered?",
      options: [
        { value: "mild", label: "Mild - easily ignored" },
        { value: "moderate", label: "Moderate - somewhat distracting" },
        { value: "strong", label: "Strong - difficult to resist" },
        { value: "overwhelming", label: "Overwhelming - nearly impossible to resist" }
      ]
    },
    {
      id: "emotional_relief",
      text: "How much emotional relief do you typically feel when shopping?",
      options: [
        { value: "minimal", label: "Minimal or none" },
        { value: "temporary", label: "Brief/temporary relief" },
        { value: "significant", label: "Significant relief while shopping" },
        { value: "substantial", label: "Substantial, lasting relief" }
      ]
    },
    {
      id: "post_purchase",
      text: "How do you typically feel shortly after an emotional purchase?",
      options: [
        { value: "better", label: "Better - the purchase improved my mood" },
        { value: "neutral", label: "Neutral - neither better nor worse" },
        { value: "guilty", label: "Guilty or regretful" },
        { value: "worse", label: "Worse than before shopping" }
      ]
    },
    {
      id: "impulse_vs_planned",
      text: "When emotionally shopping, how often are your purchases impulsive rather than planned?",
      options: [
        { value: "rarely", label: "Rarely - I mostly stick to planned purchases" },
        { value: "sometimes", label: "Sometimes impulsive" },
        { value: "often", label: "Often impulsive" },
        { value: "always", label: "Almost always impulsive" }
      ]
    },
    {
      id: "awareness",
      text: "How aware are you of the emotional triggers when you're shopping?",
      options: [
        { value: "very_aware", label: "Very aware - I recognize it happening" },
        { value: "somewhat_aware", label: "Somewhat aware" },
        { value: "minimal_awareness", label: "Minimally aware" },
        { value: "unaware", label: "Unaware in the moment - realize later" }
      ]
    },
    {
      id: "stressful_time",
      text: "During particularly stressful life periods, how does your shopping change?",
      options: [
        { value: "decreases", label: "I tend to shop less" },
        { value: "no_change", label: "No noticeable change" },
        { value: "increases", label: "I shop somewhat more" },
        { value: "greatly_increases", label: "I shop significantly more" }
      ]
    },
    {
      id: "shopping_location",
      text: "Where do you most often shop when emotionally triggered?",
      options: [
        { value: "online_mobile", label: "Online via mobile device" },
        { value: "online_computer", label: "Online via computer" },
        { value: "physical_stores", label: "In physical stores" },
        { value: "varies", label: "Varies widely depending on situation" }
      ]
    },
    {
      id: "product_category",
      text: "Which product categories do you gravitate toward when emotionally shopping?",
      options: [
        { value: "clothing", label: "Clothing, shoes, or accessories" },
        { value: "tech", label: "Electronics, gadgets, or tech" },
        { value: "home", label: "Home decor or household items" },
        { value: "self_care", label: "Self-care, beauty, or wellness products" }
      ]
    },
    {
      id: "purchase_size",
      text: "How does your emotional state affect your purchase size?",
      options: [
        { value: "smaller", label: "I tend to make smaller purchases" },
        { value: "no_effect", label: "No significant effect on purchase size" },
        { value: "larger", label: "I tend to make larger purchases" },
        { value: "multiple", label: "I tend to make multiple purchases" }
      ]
    }
  ];
  
  // Handle option selection
  const handleSelect = (value) => {
    const updatedAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(updatedAnswers);
    
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate results
      calculateResults(updatedAnswers);
    }
  };
  
  // Calculate assessment results
  const calculateResults = (finalAnswers) => {
    // Define trigger profiles
    const triggerProfiles = {
      "Stress Shopper": 0,
      "Retail Therapist": 0,
      "Boredom Browser": 0,
      "Celebratory Spender": 0,
      "Mood Booster": 0,
      "Avoidance Buyer": 0,
      "Reward Seeker": 0,
      "Social Shopper": 0
    };
    
    // Map emotional triggers to shopping behaviors
    // Frequency of emotional shopping
    if (finalAnswers.emotion_spend === "often" || finalAnswers.emotion_spend === "very_often") {
      triggerProfiles["Retail Therapist"] += 2;
      triggerProfiles["Mood Booster"] += 2;
    }
    
    // Negative emotional triggers
    if (finalAnswers.negative_emotions === "sadness") {
      triggerProfiles["Mood Booster"] += 3;
      triggerProfiles["Avoidance Buyer"] += 1;
    } else if (finalAnswers.negative_emotions === "anxiety") {
      triggerProfiles["Stress Shopper"] += 3;
      triggerProfiles["Avoidance Buyer"] += 2;
    } else if (finalAnswers.negative_emotions === "boredom") {
      triggerProfiles["Boredom Browser"] += 3;
      triggerProfiles["Social Shopper"] += 1;
    } else if (finalAnswers.negative_emotions === "loneliness") {
      triggerProfiles["Social Shopper"] += 3;
      triggerProfiles["Avoidance Buyer"] += 1;
    }
    
    // Positive emotional triggers
    if (finalAnswers.positive_emotions === "excitement") {
      triggerProfiles["Celebratory Spender"] += 2;
      triggerProfiles["Social Shopper"] += 1;
    } else if (finalAnswers.positive_emotions === "happiness") {
      triggerProfiles["Celebratory Spender"] += 3;
    } else if (finalAnswers.positive_emotions === "pride") {
      triggerProfiles["Reward Seeker"] += 3;
      triggerProfiles["Celebratory Spender"] += 1;
    } else if (finalAnswers.positive_emotions === "relief") {
      triggerProfiles["Stress Shopper"] += 2;
      triggerProfiles["Retail Therapist"] += 1;
    }
    
    // Impulse vs planned
    if (finalAnswers.impulse_vs_planned === "often" || finalAnswers.impulse_vs_planned === "always") {
      triggerProfiles["Boredom Browser"] += 2;
      triggerProfiles["Mood Booster"] += 1;
    }
    
    // Awareness
    if (finalAnswers.awareness === "unaware" || finalAnswers.awareness === "minimal_awareness") {
      triggerProfiles["Avoidance Buyer"] += 2;
    }
    
    // Stressful periods
    if (finalAnswers.stressful_time === "increases" || finalAnswers.stressful_time === "greatly_increases") {
      triggerProfiles["Stress Shopper"] += 3;
      triggerProfiles["Avoidance Buyer"] += 1;
    }
    
    // Shopping location
    if (finalAnswers.shopping_location === "online_mobile") {
      triggerProfiles["Boredom Browser"] += 1;
      triggerProfiles["Avoidance Buyer"] += 1;
    } else if (finalAnswers.shopping_location === "physical_stores") {
      triggerProfiles["Social Shopper"] += 2;
    }
    
    // Product categories
    if (finalAnswers.product_category === "clothing") {
      triggerProfiles["Mood Booster"] += 1;
      triggerProfiles["Social Shopper"] += 1;
    } else if (finalAnswers.product_category === "tech") {
      triggerProfiles["Reward Seeker"] += 2;
    } else if (finalAnswers.product_category === "home") {
      triggerProfiles["Avoidance Buyer"] += 1;
      triggerProfiles["Retail Therapist"] += 1;
    } else if (finalAnswers.product_category === "self_care") {
      triggerProfiles["Mood Booster"] += 2;
      triggerProfiles["Retail Therapist"] += 1;
    }
    
    // Purchase size
    if (finalAnswers.purchase_size === "larger") {
      triggerProfiles["Celebratory Spender"] += 1;
      triggerProfiles["Reward Seeker"] += 1;
    } else if (finalAnswers.purchase_size === "multiple") {
      triggerProfiles["Boredom Browser"] += 1;
      triggerProfiles["Mood Booster"] += 1;
    }
    
    // Emotional relief
    if (finalAnswers.emotional_relief === "significant" || finalAnswers.emotional_relief === "substantial") {
      triggerProfiles["Retail Therapist"] += 2;
      triggerProfiles["Mood Booster"] += 1;
    }
    
    // Post purchase feelings
    if (finalAnswers.post_purchase === "better") {
      triggerProfiles["Mood Booster"] += 2;
      triggerProfiles["Retail Therapist"] += 1;
    } else if (finalAnswers.post_purchase === "guilty" || finalAnswers.post_purchase === "worse") {
      triggerProfiles["Avoidance Buyer"] += 2;
    }
    
    // Find primary and secondary trigger profiles
    const sortedProfiles = Object.entries(triggerProfiles).sort((a, b) => b[1] - a[1]);
    const primaryTrigger = sortedProfiles[0][0];
    const secondaryTrigger = sortedProfiles[1][0];
    
    // Calculate trigger percentages based on points
    const totalPoints = Object.values(triggerProfiles).reduce((sum, points) => sum + points, 0);
    
    const triggerPercentages = {};
    Object.entries(triggerProfiles).forEach(([trigger, points]) => {
      triggerPercentages[trigger] = Math.round((points / totalPoints) * 100);
    });
    
    // Trigger profile definitions
    const triggerInfo = {
      "Stress Shopper": {
        description: "You tend to shop in response to stress, tension, and pressure in your life. Shopping provides a sense of control and temporary escape from overwhelming feelings.",
        emoji: "😓",
        characteristics: [
          "Shop more during stressful life periods",
          "Feel a sense of relief when browsing or buying",
          "May shop impulsively to manage anxiety",
          "Often purchase comfort items or self-care products"
        ],
        strategies: [
          "Develop alternative stress management techniques (meditation, exercise, etc.)",
          "Create a 'stress shopping' delay rule - wait 24 hours before purchasing",
          "Keep a stress journal to identify patterns and triggers",
          "Set up calendar reminders during high-stress periods to be mindful of shopping urges"
        ]
      },
      "Retail Therapist": {
        description: "You use shopping as emotional medicine, seeking a 'fix' for negative feelings. The pleasure from acquiring new things provides a temporary emotional uplift.",
        emoji: "🛍️",
        characteristics: [
          "Shop specifically to feel better when down",
          "Experience a significant mood boost during shopping",
          "May 'treat yourself' as a form of self-care",
          "Shopping feels like a reliable way to improve mood"
        ],
        strategies: [
          "Create a list of non-shopping activities that boost your mood",
          "Set a 'mood shopping' budget to limit therapeutic spending",
          "Try the 'window shopping only' challenge for one month",
          "Practice mindfulness to recognize when you're shopping for emotional reasons"
        ]
      },
      "Boredom Browser": {
        description: "You shop to fill time and alleviate boredom. Browsing and discovering items provides entertainment and stimulation when you're feeling understimulated.",
        emoji: "😑",
        characteristics: [
          "Shop when feeling unstimulated or bored",
          "Spend time browsing without specific purchase goals",
          "Often make small, impulsive purchases",
          "Shopping provides entertainment and passes time"
        ],
        strategies: [
          "Create a list of engaging non-shopping activities for bored moments",
          "Delete shopping apps from your phone to reduce easy access",
          "Set specific shopping times rather than browsing whenever bored",
          "Find hobby communities to connect with when feeling understimulated"
        ]
      },
      "Celebratory Spender": {
        description: "You use shopping to mark achievements, celebrations, and positive events. Purchases serve as personal rewards and ways to commemorate happy moments.",
        emoji: "🎉",
        characteristics: [
          "Shop when feeling happy or accomplished",
          "Use purchases as rewards for achievements",
          "Shopping enhances positive emotions",
          "May spend more during celebratory moments"
        ],
        strategies: [
          "Pre-plan celebration purchases rather than impulse buying",
          "Create tiers of celebration spending based on achievement size",
          "Consider non-material ways to celebrate victories",
          "Set aside a 'celebration fund' with specific budget limits"
        ]
      },
      "Mood Booster": {
        description: "You shop specifically to enhance your emotional state, using purchases as reliable mood enhancers when feeling down, sad, or emotionally flat.",
        emoji: "😊",
        characteristics: [
          "Shop to counteract feelings of sadness or low mood",
          "Feel a noticeable mood improvement while shopping",
          "Gravitate toward items that provide instant gratification",
          "May purchase products that enhance appearance or comfort"
        ],
        strategies: [
          "Create a 'mood emergency kit' with non-shopping mood boosters",
          "Practice emotional awareness exercises before shopping",
          "Set up mood-triggered spending limits or delays",
          "Find therapeutic support if shopping is masking deeper emotional needs"
        ]
      },
      "Avoidance Buyer": {
        description: "You shop to avoid difficult emotions or situations. Shopping serves as a distraction from uncomfortable feelings or responsibilities you'd prefer not to face.",
        emoji: "🙈",
        characteristics: [
          "Shop to escape uncomfortable feelings or tasks",
          "May be less aware of emotional triggers in the moment",
          "Shopping provides distraction from problems",
          "Often feel guilty afterward about avoiding responsibilities"
        ],
        strategies: [
          "Use the HALT method (ask if you're Hungry, Angry, Lonely, or Tired)",
          "Practice facing uncomfortable emotions in small doses",
          "Set a timer for addressing difficult tasks before allowing shopping",
          "Consider journaling about what you might be avoiding when urges strike"
        ]
      },
      "Reward Seeker": {
        description: "You use shopping to create a structured reward system for yourself. Purchases serve as tangible prizes for effort, perseverance, or enduring difficulties.",
        emoji: "🏆",
        characteristics: [
          "Shop as a reward for hard work or persistence",
          "Purchase items that feel like earned luxuries",
          "Use shopping as motivation for completing tasks",
          "May have a 'deserve it' mindset with purchases"
        ],
        strategies: [
          "Create a formalized reward system with clear spending limits",
          "Develop non-material rewards for your accomplishments",
          "Schedule periodic 'earned' shopping trips rather than spontaneous rewards",
          "Focus on quality over quantity in reward purchases"
        ]
      },
      "Social Shopper": {
        description: "You shop to connect with others or reduce feelings of isolation. Shopping provides social interaction, shared experiences, and a sense of belonging to consumer culture.",
        emoji: "👥",
        characteristics: [
          "Shop to reduce feelings of loneliness or isolation",
          "Enjoy shopping with or for others",
          "May be influenced by social media or peer purchases",
          "Shopping creates a sense of connection or belonging"
        ],
        strategies: [
          "Schedule regular social activities that don't involve shopping",
          "Join communities aligned with your interests",
          "Set social shopping budgets when shopping with friends",
          "Practice mindful social media use to reduce FOMO-driven purchases"
        ]
      }
    };
    
    // Prepare final result data
    setResultData({
      primaryTrigger: primaryTrigger,
      secondaryTrigger: secondaryTrigger,
      description: triggerInfo[primaryTrigger].description,
      emoji: triggerInfo[primaryTrigger].emoji,
      characteristics: triggerInfo[primaryTrigger].characteristics,
      strategies: triggerInfo[primaryTrigger].strategies,
      triggerPercentages: triggerPercentages,
      secondaryDescription: `You also show strong patterns of a ${secondaryTrigger}.`,
      secondaryStrategies: triggerInfo[secondaryTrigger].strategies
    });
    
    // Show results
    setShowResults(true);
  };
  
  // Go back to previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  // Restart the assessment
  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResultData(null);
  };
  
  // Prepare data for ShareStatsCard component
  const prepareShareData = () => {
    if (!resultData) return null;
    
    return {
      quizTitle: "Emotional Spending Trigger Quiz",
      result: resultData.primaryTrigger,
      resultEmoji: resultData.emoji,
      resultDescription: resultData.description,
      resultTagline: "Understanding your emotional triggers is the first step to mindful spending!",
      quizType: "emotional_spending_triggers"
    };
  };
  
  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {!showResults ? (
        <>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-3 dark:text-white">Emotional Spending Trigger Quiz</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Discover what emotional triggers drive your shopping habits and learn personalized strategies to shop more mindfully.
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span>Question {currentQuestion + 1} of {questions.length}</span>
              <span>{Math.round((currentQuestion / questions.length) * 100)}% Complete</span>
            </div>
            <Progress
              value={(currentQuestion / questions.length) * 100}
              className="h-2 bg-gray-200 dark:bg-gray-700"
              style={{
                "--progress-indicator-color": 
                  theme === "mint" ? "#10b981" : 
                  theme === "pink" ? "#ec4899" : 
                  "#8b5cf6" // lavender
              }}
            />
          </div>
          
          {/* Current question */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              {questions[currentQuestion].text}
            </h2>
            
            <div className="space-y-3">
              {questions[currentQuestion].options.map((option) => (
                <motion.button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full py-3 px-4 text-left rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                    answers[questions[currentQuestion].id] === option.value
                      ? `border-2 border-${theme}-500 dark:border-${theme}-400`
                      : ""
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {option.label}
                </motion.button>
              ))}
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <motion.button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 ${
                currentQuestion === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              whileHover={currentQuestion !== 0 ? { scale: 1.05 } : {}}
              whileTap={currentQuestion !== 0 ? { scale: 0.95 } : {}}
            >
              Previous
            </motion.button>
            
            <div className="text-sm text-gray-500 dark:text-gray-400 self-center">
              Your results are private and stored only on your device.
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 dark:text-white">Your Emotional Spending Trigger</h1>
            <div className="text-6xl mb-3">{resultData.emoji}</div>
            <h2 className="text-xl font-bold mb-1 dark:text-white">{resultData.primaryTrigger}</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-1">
              {resultData.description}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {resultData.secondaryDescription}
            </p>
          </div>
          
          {/* Trigger Breakdown */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3 dark:text-white">Your Emotional Trigger Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(resultData.triggerPercentages)
                .sort(([, a], [, b]) => b - a)
                .map(([trigger, percentage]) => (
                  <div key={trigger} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium dark:text-white">{trigger}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 h-2 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          trigger === resultData.primaryTrigger
                            ? "bg-blue-500"
                            : trigger === resultData.secondaryTrigger
                            ? "bg-purple-500"
                            : "bg-gray-400 dark:bg-gray-500"
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
          
          {/* Characteristics & Strategies */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">👁️</span> Your Shopping Characteristics
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.characteristics.map((char, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span> {char}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">💡</span> Primary Trigger Strategies
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.strategies.map((strategy, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">•</span> {strategy}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">🔄</span> Secondary Trigger Strategies
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.secondaryStrategies.map((strategy, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span> {strategy}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Daily Practice */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center dark:text-white">
              <span className="mr-2">✨</span> Daily Practice
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              Consider using this PAUSE method when you feel the urge to shop:
            </p>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start">
                <span className="bg-white dark:bg-gray-800 text-amber-600 rounded-full h-6 w-6 flex items-center justify-center text-sm font-bold mr-2 mt-0.5">P</span>
                <span><strong>Pause</strong> before acting on shopping urges (take 3 deep breaths)</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white dark:bg-gray-800 text-amber-600 rounded-full h-6 w-6 flex items-center justify-center text-sm font-bold mr-2 mt-0.5">A</span>
                <span><strong>Acknowledge</strong> the emotion you're feeling without judgment</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white dark:bg-gray-800 text-amber-600 rounded-full h-6 w-6 flex items-center justify-center text-sm font-bold mr-2 mt-0.5">U</span>
                <span><strong>Understand</strong> the trigger (what happened right before the urge?)</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white dark:bg-gray-800 text-amber-600 rounded-full h-6 w-6 flex items-center justify-center text-sm font-bold mr-2 mt-0.5">S</span>
                <span><strong>Select</strong> an alternative response from your strategy list</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white dark:bg-gray-800 text-amber-600 rounded-full h-6 w-6 flex items-center justify-center text-sm font-bold mr-2 mt-0.5">E</span>
                <span><strong>Evaluate</strong> how you feel after trying your alternative</span>
              </li>
            </ul>
          </div>
          
          {/* Share Results Card */}
          <ShareStatsCard 
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryTrigger}
            cardType="quiz"
          />

          <ShareableInfographics
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryType}
            cardType="quiz"
          />

          {/* Shop Recommendations */}
          {showResults && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2 dark:text-white flex items-center">
                <span className="mr-2">🧘</span> Trigger-Specific Resources
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {resultData.primaryTrigger === "Stress Shopper" ? 
                  "Discover stress-relief alternatives and mindful spending tools specifically for stress-triggered shopping habits." :
                resultData.primaryTrigger === "Retail Therapist" ?
                  "Explore healthier alternatives to retail therapy and resources to manage emotional spending triggers." :
                resultData.primaryTrigger === "Boredom Browser" ?
                  "Find engaging alternatives to browsing and shopping when boredom strikes, plus tools to manage idle shopping habits." :
                resultData.primaryTrigger === "Celebratory Spender" ?
                  "Browse our selection of meaningful celebration alternatives and resources for creating joy without excess spending." :
                resultData.primaryTrigger === "Mood Booster" ?
                  "Discover alternative mood-boosters and tools specifically designed for those who shop to enhance emotional states." :
                resultData.primaryTrigger === "Avoidance Buyer" ?
                  "Explore resources to help manage difficult emotions and products that support healthier coping mechanisms." :
                resultData.primaryTrigger === "Reward Seeker" ?
                  "Find alternative reward systems and resources to maintain motivation without excessive shopping." :
                resultData.primaryTrigger === "Social Shopper" ?
                  "Discover connection-building alternatives and tools for managing socially-triggered shopping impulses." :
                  "Browse personalized resources designed to help manage your specific emotional spending triggers."
                }
              </p>
              <motion.button
                onClick={() => window.location.href = '/shop'}
                className={`px-4 py-2 w-full rounded-lg text-white text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                View Personalized Alternatives
              </motion.button>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <motion.button
              onClick={handleRestart}
              className="py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Take Quiz Again
            </motion.button>
            
            <motion.button
              onClick={goToInput}
              className={`py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Track Your Purchases
            </motion.button>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This quiz is designed to help you understand your emotional spending triggers. Use these insights alongside purchase tracking for a more mindful shopping experience.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default EmotionalSpendingTriggerQuiz;
